import { Card, CardContent, Stack, Typography } from '@mui/joy';
import { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { IEmployee } from 'contacts/types';
import { FormField } from 'core/components';
import { NON_EMPTY } from 'core/constants';
import { LabeledValue, NeutralChip, TextHighlighter } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IRequestPayload } from 'core/types';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { Direction, Level, Variant } from 'theme/enums';

export enum EmployeeType {
  ISR = 'isr',
  OSR = 'osr',
}

interface IProps {
  name: EmployeeType;
  label: string;
}

export const EmployeeField = ({ name, label }: IProps) => {
  const { t } = useTranslation();
  const { setValue, watch, errors } = useFormContext();

  const fieldError = errors?.[name];

  const employeeValue = watch(name) as IEmployee;
  const hasEmployeeValue =
    employeeValue?.employeeId ||
    employeeValue?.employeeName ||
    employeeValue?.email ||
    employeeValue?.default;

  const onChange = (_: SyntheticEvent, option: ICorporateUserAutocomplete) => {
    if (!option) {
      return setValue(name, {
        employeeId: '',
        employeeName: '',
        email: '',
        default: false,
      });
    }

    setValue(name, {
      employeeId: option.externalEmployeeId || '',
      employeeName: option.name || '',
      email: option.email || '',
      default: option.default ?? false,
    });
  };

  const renderOptions = (
    option: ICorporateUserAutocomplete,
    _: (option: ICorporateUserAutocomplete) => string,
    inputValue?: string
  ) => {
    return (
      <Stack>
        <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <TextHighlighter level={Level.TITLE_MD} text={option.name} highlight={inputValue} />
          {option.externalEmployeeId && (
            <NeutralChip>
              <TextHighlighter
                level={Level.TITLE_SM}
                text={option.externalEmployeeId}
                highlight={inputValue}
              />
            </NeutralChip>
          )}
        </Stack>

        <Typography level={Level.BODY_SM}>{option.email}</Typography>
      </Stack>
    );
  };

  return (
    <>
      <FormField
        name={name}
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          placeholder: label,
          getOptions: (requestPayload: IRequestPayload) =>
            corporateUserService.getCorporateUsersAutocomplete({
              ...requestPayload,
              filters: {
                ...requestPayload?.filters,
                externalEmployeeId: NON_EMPTY,
                ...(name === EmployeeType.ISR && { includeDefaultIsr: 1 }),
                ...(name === EmployeeType.OSR && { includeDefaultOsr: 1 }),
              },
            }),
          getLabel: (opt: ICorporateUserAutocomplete) => opt?.name,
          optionKey: 'externalEmployeeId',
          error: fieldError,
          onChange: onChange,
          renderOptions: renderOptions,
          value: hasEmployeeValue
            ? {
                externalEmployeeId: employeeValue.employeeId,
                name: employeeValue.employeeName,
                email: employeeValue.email,
                default: employeeValue.default,
              }
            : null,
        }}
      />

      <Card variant={Variant.PLAIN}>
        <CardContent>
          <Stack direction={Direction.COLUMN} gap={1}>
            <LabeledValue label={t('labelEmployeeId')} value={employeeValue?.employeeId} />
            <LabeledValue label={t('labelName')} value={employeeValue?.employeeName} />
            <LabeledValue label={t('labelEmail')} value={employeeValue?.email} />
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};
